import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, RIBBON, type RibbonKey } from '../body';
import type { Worn } from '../headwear';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

/**
 * The Pumpkin set (ADR-0112): a jack-o'-lantern over the whole head, a pumpkin-patch tunic and a pumpkin-orange gun.
 *
 * The head is headwear tied on (`Worn`, ADR-0089), not a hat that flies off: the carved face is the face (`mask`: no
 * glasses or earring over it), so it must move with the head (a hat lags a frame and would show skin under it), and its
 * knock-out is the pumpkin smashed on the dead pose (the set's flourish bursts it, ADR-0112), which a flying hat would
 * leave on a bare head. It is a lobed ball ray-cast in the game's 3/4 view (`rind`: lit gold on the top-left, orange,
 * rust in the shade, a crease between lobes), so the standing and the smashed pumpkin agree; its face, stem, leaf,
 * tendril and the team's bow are drawn by hand in picture space. Standing it is the gardener's silhouette at game zoom:
 * a round orange ball, wider and lower than any hat's crown, a leaf and a hooked stem on top, the bow in the team's
 * colour; its long ends stream from behind it on the run.
 *
 * Rubric (`looks hat` / `looks outfit` at 3× and 1:1 on every team, the wardrobe pictures, the stage and a 5v5 at
 * 1280×720): head S5 R5 P4 L4 C4 A4 F4, tunic S4 R4 P5 L4 C4 A4 F4, gun skin R5 P5 F4. Still weak: the tendril is a 1-px curl that only the menus' big gardeners show.
 * Smashed (the dead pose, 4× and 1:1 on every team, the `sets` playtest): S4 R4 P4 L4 C4 F4; still weak: its guts are a
 * few specks at 1:1 (the burst's seeds say it more), and the bowl's middle can't be broken lower than the face it covers.
 */

// --- The pumpkin, a lobed ball seen from the game's 3/4 view ---------------------------------------------------------

/** Lobes round the pumpkin, how squat it is (its height over its width), how deep the creases between lobes are. */
const LOBES = 10;
const SQUASH = 0.68;
const CREASE = 0.07;
/** The middle of a lobe faces this way, round from the front (radians, to the right: the face is carved on it). */
const FRONT = 0.34;
/** Light from the top-left, a little toward us. */
const LIGHT = (() => {
  const l = [-0.65, 0.62, 0.42];
  const n = Math.hypot(...l);
  return l.map((v) => v / n) as [number, number, number];
})();

/** Which lobe a longitude is on, and where across it (0 to 1, ½ its middle). */
function lobe(phi: number): { i: number; t: number } {
  const k = (LOBES * (phi - FRONT)) / (2 * Math.PI) + 0.5;
  const i = Math.floor(k);
  return { i: ((i % LOBES) + LOBES) % LOBES, t: k - i };
}
/** The cross-section's radius at a longitude: round lobes meeting in sharp creases. */
const radius = (phi: number) => 1 - CREASE + CREASE * Math.sqrt(Math.sin(Math.PI * lobe(phi).t));
/** Below 0 inside the pumpkin (its own units: radius 1, y up the stem, z toward the front). */
const field = (x: number, y: number, z: number) =>
  (x * x + z * z) / radius(Math.atan2(x, z)) ** 2 + (y / SQUASH) ** 2 - 1;

interface View {
  w: number;
  h: number;
  /** The centre in the canvas and the radius, px. */
  cx: number;
  cy: number;
  r: number;
  /** The top tipped toward us (radians), then the whole turned in the picture (radians, counter-clockwise). */
  tilt: number;
  roll: number;
}

/**
 * The pumpkin's skin in a view, ray-cast per pixel: its tone by the light on it (a lit gold, the orange, rust in the
 * shade, brown deepest), and a crease one tone deeper wherever the lobe changes from the pixel left of it or above it.
 */
function rind(v: View): PixelCanvas {
  const [ct, st, cr, sr] = [Math.cos(v.tilt), Math.sin(v.tilt), Math.cos(v.roll), Math.sin(v.roll)];
  /** Picture space (x right, y up, z toward us) to the pumpkin's own. */
  const own = (x: number, y: number, z: number): [number, number, number] => {
    const [x1, y1] = [x * cr + y * sr, -x * sr + y * cr];
    return [x1, y1 * ct + z * st, -y1 * st + z * ct];
  };
  /** The pumpkin's own direction in picture space. */
  const seen = (x: number, y: number, z: number): [number, number, number] => {
    const [y1, z1] = [y * ct - z * st, y * st + z * ct];
    return [x * cr - y1 * sr, x * sr + y1 * cr, z1];
  };
  const lobes: (number | null)[] = [];
  const c = new PixelCanvas(v.w, v.h);
  for (let py = 0; py < v.h; py++) {
    for (let px = 0; px < v.w; px++) {
      const X = (px + 0.5 - v.cx) / v.r;
      const Y = (v.cy - py - 0.5) / v.r;
      const inside = (z: number) => field(...own(X, Y, z)) < 0;
      // It lies inside the unit sphere: march from the sphere's front to its back.
      const front = Math.sqrt(Math.max(0, 1 - X * X - Y * Y));
      let z = front;
      while (z > -front && !inside(z)) z -= 0.04;
      if (z <= -front) {
        lobes.push(null);
        continue;
      }
      let [hi, lo] = [z + 0.04, z];
      for (let k = 0; k < 8; k++) {
        const mid = (hi + lo) / 2;
        if (inside(mid)) lo = mid;
        else hi = mid;
      }
      const p = own(X, Y, lo);
      const e = 1e-3;
      const n = seen(
        field(p[0] + e, p[1], p[2]) - field(p[0] - e, p[1], p[2]),
        field(p[0], p[1] + e, p[2]) - field(p[0], p[1] - e, p[2]),
        field(p[0], p[1], p[2] + e) - field(p[0], p[1], p[2] - e),
      );
      const light = (n[0] * LIGHT[0] + n[1] * LIGHT[1] + n[2] * LIGHT[2]) / Math.hypot(...n);
      // Round the stem the creases crowd together: no lobes there.
      lobes.push(Math.abs(p[1] / SQUASH) < 0.93 ? lobe(Math.atan2(p[0], p[2])).i : -1);
      c.set(px, py, light > 0.6 ? 'Y' : light > 0.05 ? 'o' : light > -0.4 ? 'u' : 'n');
    }
  }
  const DEEPER: Partial<Record<PaletteKey, PaletteKey>> = { Y: 'o', o: 'u', u: 'n', n: 'N' };
  const out = c.clone();
  for (let y = 0; y < v.h; y++) {
    for (let x = 0; x < v.w; x++) {
      const i = lobes[y * v.w + x];
      if (i === null || i === undefined || i < 0) continue;
      const left = x > 0 ? lobes[y * v.w + x - 1] : null;
      const up = y > 0 ? lobes[(y - 1) * v.w + x] : null;
      if ((left != null && left >= 0 && left !== i) || (up != null && up >= 0 && up !== i))
        out.set(x, y, DEEPER[c.get(x, y)!]!);
    }
  }
  return out;
}

/** A mask from a grid of `#` (set) and anything else (clear), as a canvas (`k` where set). */
const mask = (src: string) => PixelCanvas.fromGrid(src.replace(/#/g, 'k').replace(/[^k\s]/g, '.'));

/**
 * The carved face, as it shows on the pumpkin standing (picture space, so its edges step cleanly): a triangle eye
 * near, a narrower one far (the face turned right, in 3/4), and a grin turned up to points, a tooth down from its top
 * and one up from its bottom (`o`: left standing).
 */
const FACE = mask(`
  ......##............##......
  .....####..........###......
  ....######........#####.....
  ...########......######.....
  ..##########....########....
  .############..#########....
  ............................
  ............................
  ............................
  #.........................#.
  ##.......................##.
  .###...................###..
  .#######ooo###############..
  ..######ooo##############...
  ...#####################....
  ....###################.....
  ......#########ooo###.......
  ........#######ooo#.........
`);
const FACE_AT = [23, 24] as const;

/**
 * Cuts `holes` out of `c` (at x, y): the cut's edge a dark rim (darkest on its top edge, the lip's shadow), the inside
 * lit pale yellow by the candle.
 */
function carve(c: PixelCanvas, holes: PixelCanvas, x0: number, y0: number): void {
  const hole = (x: number, y: number) => holes.get(x, y) !== null;
  for (let y = 0; y < holes.h; y++) {
    for (let x = 0; x < holes.w; x++) {
      if (!hole(x, y) || c.get(x + x0, y + y0) === null) continue;
      const inner = hole(x - 1, y) && hole(x + 1, y) && hole(x, y - 1) && hole(x, y + 1);
      c.set(x + x0, y + y0, inner ? 'y' : hole(x, y - 1) ? 'N' : 'k');
    }
  }
}

/**
 * A vine tendril on `c` (at `TOP_AT`), 1 px of dark green over the pumpkin's skin: from the stem's foot (`from`, frame
 * px) out over the shoulder, curling in on itself round `eye` from `r` px out.
 */
function tendril(c: PixelCanvas, from: readonly [number, number], eye: readonly [number, number], r: number) {
  const [x0, y0] = [from[0] - TOP_AT[0], from[1] - TOP_AT[1]];
  const [cx, cy] = [eye[0] - TOP_AT[0], eye[1] - TOP_AT[1]];
  const pts: [number, number][] = [];
  for (let t = 0; t <= 1; t += 0.05) pts.push([x0 + (cx - r - x0) * t, y0 + (cy - y0) * t * t]);
  for (let a = 0; a <= 1.7 * Math.PI; a += 0.15) {
    const k = r - (a / (1.7 * Math.PI)) * (r - 1.2);
    pts.push([cx - k * Math.cos(a), cy - k * Math.sin(a)]);
  }
  for (let i = 1; i < pts.length; i++) c.line(...pts[i - 1]!, ...pts[i]!, 'G');
  return c;
}

/** What sits on top (picture space, at `TOP_AT`): the twisted stem and the team's bow round it. */
const TOP_AT = [10, 2] as const;
const STEM = new PixelCanvas(44, 20)
  // The stem, twisted, lit on the left, hooked over to the right, its cut end pale.
  .stamp(
    `
    ....lggggm....
    ...lgGGGGGmt..
    ...lgGgGDDnmt.
    ..lgGgGD..nm..
    ..lgGGgD......
    .lgGgGGgD.....
    .lgGgGGgGD....
    lggGgGGgGD....
  `,
    16,
    4,
  )
  // The team's ribbon tied round it in a bow, its ends falling over the top.
  .stamp(
    `
    .cc.......cc.
    cbbc.....cbbB
    cbbbcbbbcbbbB
    cbbbbcbBbbbBB
    .cbbBBbBBbBB.
    ...BbB.bbB...
    ..bbB...bB...
  `,
    14,
    9,
  );

/** The leaf off the stem, up over the top on the left: lit along its top edge, its midrib down to the stalk. */
const LEAF = new PixelCanvas(44, 20).stamp(
  `
    ...........ll..
    ........llllgg.
    ......llgggggG.
    ....llggggggGG.
    ..llgggggggGGG.
    .lggggggGGgGGG.
    lgggggGGgggGG..
    .gggGGgggGGG...
    ..GGgggGGGG....
    ....GGGGG......
  `,
  3,
  4,
);
const TOP = tendril(new PixelCanvas(44, 20), [26, 15], [44.5, 21], 3).blit(LEAF).blit(STEM);

/** The pumpkin on the head: over the face in the hair's place, a little lower and wider, so it sits on the shoulders. */
const STANDING: View = { w: 64, h: 52, cx: 31.5, cy: 30, r: 23.5, tilt: 0.3, roll: 0 };
const standingHead = (): PixelCanvas => {
  const head = rind(STANDING);
  carve(head, FACE, ...FACE_AT);
  return head.blit(TOP, ...TOP_AT);
};

/**
 * On the dead pose's head: the pumpkin smashed open on the grass. Its bowl (`BOWL`, the head's lower half) stands open
 * toward us, its rim broken in jagged teeth with two shards of its side standing at the ends, the pale flesh along the
 * break, the dark hollow inside with lumps of pulp and seeds; its lid (`LID`), broken off round the stem, lies apart on
 * the left with the stem and the team's bow; pulp spills over the front, a shard and a chip lie by it. No face: the
 * carving went with the top. It covers the dead face whole (a break lower in its middle would show the face).
 */
const BOWL: View = { w: 64, h: 72, cx: 42, cy: 56.5, r: 18.5, tilt: 0.35, roll: 0 };
/** The break round the bowl (frame px, from x `BREAK_X`): its back rim's top and its front rim's, each row by column. */
const BREAK_X = 23;
// biome-ignore format: a row of the picture
const BACK_RIM = [52,51,50,49,48,47,46,47,48,46,45,44,44,44,45,44,43,42,42,43,44,43,42,43,44,45,44,45,46,47,46,47,48,49,49,50,51,52];
// biome-ignore format: a row of the picture
const FRONT_RIM = [52,53,53,54,54,55,55,56,56,55,56,57,57,58,56,54,56,58,58,57,58,58,57,58,59,60,59,60,59,57,56,56,54,54,53,53,52,52];

/**
 * The bowl broken open (on `c`, the whole pumpkin's skin): above the back rim the top is gone; the rims are its pale
 * broken flesh; between them its hollow, the back wall's inside lit at the top, dark down in it.
 */
function breakOpen(c: PixelCanvas): PixelCanvas {
  for (const [i, back] of BACK_RIM.entries()) {
    const x = BREAK_X + i;
    const front = FRONT_RIM[i]!;
    for (let y = 0; y < front; y++) {
      if (c.get(x, y) === null) continue;
      c.set(x, y, y < back ? null : y === back ? 'e' : y - back < 3 ? 'u' : y - back < 5 || front - y < 3 ? 'n' : 'N');
    }
    // The front's broken edge, pale on top, its flesh under it.
    if (c.get(x, front) !== null) c.set(x, front, 'e');
    if (c.get(x, front + 1) !== null) c.set(x, front + 1, 't');
  }
  // Its guts: lumps of pulp in the hollow, seeds in them (cream, shaded below and on the right).
  const hollow = (x: number, y: number) => c.get(x, y) === 'N' || c.get(x, y) === 'n';
  const paint = (src: string, x0: number, y0: number) => {
    const g = grid(src);
    for (let y = 0; y < g.h; y++)
      for (let x = 0; x < g.w; x++)
        if (g.get(x, y) !== null && hollow(x0 + x, y0 + y)) c.set(x0 + x, y0 + y, g.get(x, y));
  };
  paint(
    `
    ..........................ee.
    ...ee......Uo.........ee..et.
    ...et.....UooU..ee....et.....
    ...........UU...et...........
    .....Uo...............UoU....
    ....UooU.....ee......UooU....
    .....UU......et.......UU..ee.
    ...........................et
  `,
    27,
    48,
  );
  // Two shards of its side still standing at either end of the break, pointing up past it.
  c.stamp(
    `
    .Y....
    .YY...
    YYoe..
    Yooe..
    Yooe..
    YooeN.
    Yoooe.
    .Yooe.
    ..ooe.
  `,
    22,
    43,
  ).stamp(
    `
    ....u.
    ...uu.
    ..euu.
    ..eouu
    .eoouu
    .eoouu
    .eooun
    ..oun.
  `,
    56,
    43,
  );
  // Cracks down the front from two of its notches.
  for (const crack of CRACKS)
    for (const [x, y] of crack) if (c.get(x, y) !== null && c.get(x, y) !== 'e') c.set(x, y, 'N');
  // Its guts spilt over the front on the left and onto the grass, seeds in them; a shard broken off on the right.
  return c
    .stamp(
      `
    ..YY...
    .YyoY..
    .YoeY..
    ..YoY..
    ..YyoY.
    ..YotY.
    .YYooY.
    YyoeooY
    YoootoY
    .YYYYY.
  `,
      27,
      55,
    )
    .stamp(
      `
    ...eoo
    ..eYoo
    .eYoou
    eYoouu
    .ouun.
    ..nn..
  `,
      55,
      62,
    )
    .stamp(
      `
    .Yoe..
    Yoouee
    .ouu..
  `,
      53,
      36,
    );
}

// biome-ignore format: points in the picture
const CRACKS: [number, number][][] = [[[36, 59], [36, 60], [37, 61], [37, 62], [36, 63]], [[44, 59], [45, 60], [45, 61], [44, 62], [44, 63], [45, 64]]];

/**
 * The lid broken off round the stem (in the standing frame's place: the stem and the bow sit on it as they stood): a
 * dome of rind lit on the left, ribbed, its broken edge pale and jagged, the dark inside showing under it.
 */
const LID = new PixelCanvas(64, 40)
  .stamp(
    `
    .....YYYYYooooo.....
    ...YYYYYoooYoooou...
    ..YYYYoooooYooouuu..
    .YYYoooYooooYoouuun.
    .YYooooYoooooYouuun.
    eYoeooeYooeoooeouuee
    .eNeeoNeeNNeeoeNeeN.
    ..NN..NNN..NNN.NN...
  `,
    21,
    14,
  )
  .blit(STEM, ...TOP_AT);
/** Where the lid lies, moved from its place on the head: on the grass behind the bowl, on the left. */
const LID_AT = [-13, 19] as const;

const lyingHead = (): PixelCanvas => new PixelCanvas(64, 72).blit(LID, ...LID_AT).blit(breakOpen(rind(BOWL)));

/**
 * The bow's long ends (the sun hat's ribbon): standing, they hang down the pumpkin's back, out of sight; on the run they
 * stream out behind it from under the leaf.
 */
const HIDDEN: Part = [new PixelCanvas(1, 1), 0, 0];
const streaming = (k: 'flowA' | 'flowB' | 'flowC'): Part => {
  const [c, x, y] = RIBBON[k];
  return [c, x + 2, y - 8];
};
const TAILS: Record<RibbonKey, Part> = {
  hang: HIDDEN,
  hang2: HIDDEN,
  flowA: streaming('flowA'),
  flowB: streaming('flowB'),
  flowC: streaming('flowC'),
};

// --- The pumpkin patch: a vine-green tunic, the pumpkin's leaves spread over the shoulders ---------------------------

/**
 * Standing (at (6, 41)): two big pumpkin leaves fanning out from under the head over the shoulders (lit on the left,
 * in shade on the right, a pale midrib), a dark green tunic lit down its left edge, a neckline in shadow under the chin,
 * a vine across the chest, a rope belt and a short hem cut in leaf points, so the team's trousers show from the hips.
 * Its greens and the rope keep off the skin's keys and the team's.
 */
const PATCH = grid(`
    ......llll..............................gggg......
    ...llggggl..............................GGGGgg....
    .lgggggggg..............................GGGGGGGg..
    llllllllgglgGGGGGlDDDDDDDDDDDDDDGGGGGGGGGGgggggggg
    .GGGGGGGlgGGGGGGGGlDDDDDDDDDDDDGGGGGGGGGDGDDDDDDD.
    ...GGGGGGGGGGGlGGGGGlgDDDDDDDDgGGGGGGGGDDDDDDDD...
    .....DDDDGGGGlgGGGGGGGlggggggGGGGGGGGGGGDDDDD.....
    .......gGGGGlgGGGGGGlGGGGGGGGGGGGGGGGGDD..........
    ..........gGGlgGGGGGGlgGGGGGGGGGGGGGGDDD..........
    ..........UmUUmUUmUUmUUmUUmUUmUUmUUmUUmU..........
    ..........gGGGDgGGGGDgGGGGDgGGGGDgGGGGDD..........
    ...........gGD..gGGD..gGGD..gGGD..gGGD............
    ............G....GD....GD....GD....GD.............
`);

/** Lying: the rope belt at the waist (left), the tunic under the head, a leaf either side. */
const DEAD_PATCH = grid(`
    ....gGGG
    ...gGGGG
    UgggggGG
    mGGGGGGG
    UGGlGGGG
    UGGGlGGG
    mGGGGlGG
    UGGGGGlG
    UGGGGGGG
    mGGGGGGG
    UGGGGGGG
    UGGGGGGG
    mGGGGGGG
    UDDDDDDG
    UDDDDDD.
    mDDDDDDD
    UDDDGGGG
    ....DDDD
`);

const PATCH_OUTFIT: Outfit = { torso: [PATCH, 6, 41], dead: [DEAD_PATCH, 24, 51] };

const PUMPKIN_HEAD = (): Worn => ({
  worn: true,
  front: [standingHead(), 0, 0],
  ribbon: TAILS,
  dead: [lyingHead(), 0, 0],
  mask: true,
});

/**
 * The Pumpkin gun skin: the steel a pumpkin's orange (`o`, its glints gold `Y`, its darkest steps brown so the outline
 * stays dark), the brass the same orange, the wood its stem (dark brown, rimmed in dark green). Ember is red and Gold
 * yellow; this is the one orange, and the green stays on the wood's rim, out of the grass's way.
 */
const PUMPKIN_GUN = {
  k: 'N',
  '1': 'N',
  '2': 'n',
  '3': 'o',
  '4': 'o',
  '5': 'Y',
  N: 'D',
  n: 'G',
  m: 'n',
  t: 'm',
  e: 't',
  U: 'u',
  Y: 'o',
  y: 'Y',
} as const;

/** The pumpkin set (ADR-0112). */
export const PUMPKIN = drawnLater({ head: PUMPKIN_HEAD, outfit: () => PATCH_OUTFIT, gun: PUMPKIN_GUN });
